import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Spin, message, Tag } from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined, ExperimentOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import EmailAutomationService from '../../../services/EmailAutomationService';
import EmailTemplateService from '../../../services/EmailTemplateService';
import EmailCampaignService from '../../../services/EmailCampaignService';
import SurveyService from '../../../services/SurveyService';
import ROUTES from '../../../constants/routes';
import {
  BuilderShell, TopBar, StepDots, Body, FooterBar
} from './styled';
import StepBasics from './StepBasics';
import StepTrigger from './StepTrigger';
import StepSequence from './StepSequence';
import StepSettings from './StepSettings';

const STEPS = [
  { n: 1, label: 'Basics' },
  { n: 2, label: 'Trigger' },
  { n: 3, label: 'Sequence' },
  { n: 4, label: 'Settings' }
];

const mkId = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 8)}`;

const defaultAutomation = () => ({
  name: '',
  description: '',
  category: 'General',
  triggerEvent: '',
  triggerFilters: [],
  status: 'draft',
  nodes: [{ id: mkId('start'), type: 'start', position: { x: 80, y: 80 }, data: { label: 'Start' } }],
  edges: [],
  settings: {
    quietHours: { enabled: false, startHour: 21, endHour: 8, timezone: 'America/Chicago' },
    enrollment: { policy: 'once', onAlreadyEnrolled: 'skip', blockIfInOther: false },
    execution: { maxPerContact: 1, cooldownHours: 24 },
    rateLimits: { maxConcurrent: 0, maxPerHour: 0, maxPerDay: 0, staggerSeconds: 0 },
    channels: { email: true, sms: false, phone: false }
  }
});

const AutomationBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [automation, setAutomation] = useState(defaultAutomation());
  const [savedId, setSavedId] = useState(id || null);

  const [triggers, setTriggers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [verifiedSenders, setVerifiedSenders] = useState([]);
  const [surveys, setSurveys] = useState([]);

  /* --- load lookup data --- */
  useEffect(() => {
    EmailAutomationService.getTriggerEvents().then((res) => {
      if (res.success) setTriggers(res.data?.triggers || []);
    });
    EmailTemplateService.listTemplates({ limit: 100 }).then((res) => {
      if (res.success) setTemplates(res.data?.templates || []);
    });
    EmailCampaignService.getVerifiedSenders().then((res) => {
      if (res.success) setVerifiedSenders(res.data?.senders || []);
    });
    SurveyService.listSurveys({ limit: 100 }).then((res) => {
      if (res.success) setSurveys(res.data?.surveys || []);
    });
  }, []);

  /* --- load existing automation --- */
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      const res = await EmailAutomationService.getAutomation(id);
      if (cancelled) return;
      if (res.success) {
        const def = defaultAutomation();
        setAutomation({
          ...def,
          ...res.data,
          settings: { ...def.settings, ...(res.data.settings || {}) },
          nodes: res.data.nodes?.length ? res.data.nodes : def.nodes,
          edges: res.data.edges || []
        });
      } else {
        message.error(res.error);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id, isNew]);

  const update = useCallback((patch) => {
    setAutomation((a) => ({ ...a, ...patch }));
  }, []);

  const [appliedTemplateKey, setAppliedTemplateKey] = useState(null);

  const handleApplyTemplate = (tpl) => {
    const built = tpl.build();
    setAutomation((a) => ({
      ...a,
      name: a.name?.trim() ? a.name : tpl.name,
      description: a.description?.trim() ? a.description : (tpl.description || ''),
      category: tpl.category || a.category,
      triggerEvent: tpl.triggerEvent || a.triggerEvent,
      nodes: built.nodes,
      edges: built.edges
    }));
    setAppliedTemplateKey(tpl.key);
    message.success(`"${tpl.name}" applied — head to step 3 to customize.`);
  };

  /* --- save --- */
  const validate = () => {
    if (!automation.name?.trim()) { message.warning('Please give the automation a name.'); setStep(1); return false; }
    if (!automation.triggerEvent) { message.warning('Pick a trigger event.'); setStep(2); return false; }
    if (!automation.nodes?.some((n) => n.type === 'start')) {
      message.warning('Sequence must include a Start node.'); setStep(3); return false;
    }
    return true;
  };

  const save = async (statusOverride) => {
    if (!validate()) return null;
    setSaving(true);
    const payload = { ...automation };
    if (statusOverride) payload.status = statusOverride;
    const res = savedId
      ? await EmailAutomationService.updateAutomation(savedId, payload)
      : await EmailAutomationService.createAutomation(payload);
    setSaving(false);
    if (!res.success) { message.error(res.error || 'Failed to save'); return null; }
    message.success(statusOverride === 'active' ? 'Automation saved & activated' : 'Automation saved');
    if (!savedId && res.data?._id) setSavedId(res.data._id);
    setAutomation((a) => ({ ...a, status: payload.status }));
    return res.data;
  };

  const handleTest = async () => {
    if (!savedId) {
      message.info('Save the automation first, then run a test.');
      return;
    }
    const res = await EmailAutomationService.testFire(savedId);
    if (res.success) message.success('Test fired — check your inbox.');
    else message.error(res.error || 'Test failed');
  };

  const dots = useMemo(() => (
    <StepDots>
      {STEPS.map((s, i) => (
        <React.Fragment key={s.n}>
          <span className={`dot ${step === s.n ? 'active' : step > s.n ? 'done' : ''}`}>
            {step > s.n ? '✓' : s.n}
          </span>
          <span className={`label ${step === s.n ? '' : 'muted'}`}>{s.label}</span>
          {i < STEPS.length - 1 && <span className="sep">›</span>}
        </React.Fragment>
      ))}
    </StepDots>
  ), [step]);

  if (loading) {
    return (
      <BuilderShell>
        <Body style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" />
        </Body>
      </BuilderShell>
    );
  }

  return (
    <BuilderShell>
      <TopBar>
        <div className="left">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(ROUTES.ADMIN_EMAIL_AUTOMATIONS)}
            aria-label="Back"
          >
            <span className="hide-mobile">Back</span>
          </Button>
          <h2>{automation.name || (isNew ? 'New Automation' : 'Edit Automation')}</h2>
          <Tag
            className="hide-mobile"
            color={
              automation.status === 'active' ? 'green'
                : automation.status === 'paused' ? 'orange' : 'default'
            }
          >
            {(automation.status || 'draft').toUpperCase()}
          </Tag>
        </div>
        <div className="right">
          <span className="step-pill">Step {step} of {STEPS.length}</span>
          <span className="hide-mobile">{dots}</span>
          <Button
            className="hide-mobile"
            icon={<ExperimentOutlined />}
            onClick={handleTest}
          >
            Test
          </Button>
        </div>
      </TopBar>

      <Body className={step === 3 ? 'no-padding' : ''}>
        {step === 1 && (
          <StepBasics
            automation={automation}
            update={update}
            onApplyTemplate={handleApplyTemplate}
            appliedTemplateKey={appliedTemplateKey}
          />
        )}
        {step === 2 && (
          <StepTrigger
            automation={automation}
            update={update}
            triggers={triggers}
          />
        )}
        {step === 3 && (
          <StepSequence
            automation={automation}
            update={update}
            templates={templates}
            verifiedSenders={verifiedSenders}
            surveys={surveys}
          />
        )}
        {step === 4 && (
          <StepSettings automation={automation} update={update} />
        )}
      </Body>

      <FooterBar>
        <Button
          disabled={step === 1}
          onClick={() => setStep((s) => Math.max(1, s - 1))}
        >
          ← Previous
        </Button>
        <div className="footer-actions" style={{ display: 'flex', gap: 10 }}>
          <Button
            icon={<SaveOutlined />}
            loading={saving}
            onClick={() => save('draft')}
          >
            Save as Draft
          </Button>
          {step < STEPS.length ? (
            <Button
              type="primary"
              onClick={() => setStep((s) => Math.min(STEPS.length, s + 1))}
              style={{ background: '#1f2937', borderColor: '#1f2937' }}
            >
              Next →
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={saving}
              onClick={async () => {
                const ok = await save('active');
                if (ok) navigate(ROUTES.ADMIN_EMAIL_AUTOMATIONS);
              }}
              style={{ background: '#10b981', borderColor: '#10b981' }}
            >
              Save & Activate
            </Button>
          )}
        </div>
      </FooterBar>
    </BuilderShell>
  );
};

export default AutomationBuilder;
